import moment from "moment";
import { ColumnType } from "antd/lib/table";
import { DATETIME_FORMAT, EMPTY, ENTITLEMENT_SOURCE_MAP, ENTITLEMENT_STATUS_MAP } from "constants/common";
import Icon from "@ant-design/icons/lib/components/Icon";
import { EntitlementModel } from "types/model/entitlement";
import { Button, Tooltip, Typography } from "antd";
import { LongArrowup } from "assets/icons";
import { useHistory } from "react-router";
import Actions from "./Actions";

const { Text } = Typography;

function renderLanguageName(languageName: string, entity: EntitlementModel) {
  if (entity.isUpgradedToAllAccess) {
    return (
      <Tooltip title="Upgraded from premium subscription to all access" overlayInnerStyle={{ width: "max-content" }}>
        <Icon component={LongArrowup} />
        <Text>{languageName}</Text>
      </Tooltip>
    );
  }
  return languageName;
}

export const renderEntitlementType = (item: EntitlementModel) => {
  if (item.source === "ECOM_GIFT") {
    return "Gift Subscription";
  }
  return item.isSubscription ? "Subscription" : "Lifetime";
};

const statusColumn: ColumnType<EntitlementModel> = {
  title: "Status",
  dataIndex: "status",
  width: 110,
  ellipsis: true,
  render: (value) => ENTITLEMENT_STATUS_MAP.get(value) || EMPTY,
};

const sourceColumn: ColumnType<EntitlementModel> = {
  title: "Source",
  dataIndex: "source",
  width: 120,
  ellipsis: true,
  render: (value) => ENTITLEMENT_SOURCE_MAP.get(value) || EMPTY,
};

const expirationDateColumn: ColumnType<EntitlementModel> = {
  title: "Expiration date",
  dataIndex: "expirationDate",
  width: 150,
  ellipsis: true,
  render: (value) => (value ? moment(value).format(DATETIME_FORMAT) : EMPTY),
};

const createdAtColumn: ColumnType<EntitlementModel> = {
  title: "Created at",
  dataIndex: "createdDate",
  width: 150,
  ellipsis: true,
  defaultSortOrder: "descend",
  sortDirections: ["descend", "ascend", "descend"],
  sorter: (a, b) => a.createdDate - b.createdDate,
  render: (value) => (value ? moment(value).format(DATETIME_FORMAT) : EMPTY),
};

const actionsColumn: ColumnType<EntitlementModel> = {
  title: "Action",
  width: 100,
  ellipsis: true,
  onCell: () => ({ onClick: (e) => e.stopPropagation() }),
  render: (record) => <Actions record={record} />,
};

export const useColumnsB2C = (): ColumnType<EntitlementModel>[] => {
  const history = useHistory();

  return [
    {
      title: "Product ISBN",
      dataIndex: "isbn",
      width: 150,
      ellipsis: true,
      render: (value, record) =>
        record.productId ? (
          <Button
            type="link"
            style={{ padding: 0, height: "auto" }}
            onClick={() => history.push(`/products/content/${record.productId}`)}
          >
            {value || EMPTY}
          </Button>
        ) : (
          value || EMPTY
        ),
    },
    {
      title: "Product name",
      dataIndex: "productName",
      width: 300,
      ellipsis: true,
      render: renderLanguageName,
    },
    {
      title: "Purchase type",
      width: 150,
      ellipsis: true,
      render: (item: EntitlementModel) => renderEntitlementType(item),
    },
    statusColumn,
    sourceColumn,
    expirationDateColumn,
    createdAtColumn,
    actionsColumn,
  ];
};

export const useColumnsB2B = (): ColumnType<EntitlementModel>[] => {
  return [
    {
      title: "Product ISBN",
      dataIndex: "isbn",
      width: 150,
      ellipsis: true,
      render: (value) => value || EMPTY,
    },
    {
      title: "Product name",
      dataIndex: "productName",
      width: 250,
      ellipsis: true,
      render: renderLanguageName,
    },
    statusColumn,
    sourceColumn,
    expirationDateColumn,
    createdAtColumn,
    actionsColumn,
  ];
};
